import React, { useState } from 'react';
import { TextFieldProps } from '@mui/material';
import { CustomAutocomplete } from './CustomAutocomplete';
import { IllioSearchOption } from '../../../types/generatedTypes';

interface IllioSearchCellProps {
  rowId: string;
  selectedOption: IllioSearchOption | null;
  options: IllioSearchOption[];
  loading: boolean;
  onSearch: (rowId: string, value: string) => void;
  onChange: (rowId: string, value: IllioSearchOption | null) => void;
  placeholder?: string;
  className?: string;
  textFieldProps?: Partial<TextFieldProps>;
}

// Format option label - same format for dropdown and selected value
const formatOptionLabel = (option: IllioSearchOption) =>
  `${option.name} [${option.code}] [${option.currency_code}] [${option.instrument_type}]`;

export const IllioSearchCell: React.FC<IllioSearchCellProps> = ({
  rowId,
  selectedOption,
  options,
  loading,
  onSearch,
  onChange,
  placeholder = 'Search Illio instruments...',
  className,
  textFieldProps,
}) => {
  // Initialize to current selection in full format
  const [inputValue, setInputValue] = useState(
    selectedOption ? formatOptionLabel(selectedOption) : ''
  );

  const handleInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: string
  ) => {
    // Only update on user input - ignore 'reset' from MUI
    if (reason === 'input') {
      setInputValue(value);
      onSearch(rowId, value);
    } else if (reason === 'clear') {
      setInputValue('');
      onSearch(rowId, '');
    }
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    value: IllioSearchOption | null
  ) => {
    onChange(rowId, value);
    setInputValue(value ? formatOptionLabel(value) : '');
  };

  return (
    <CustomAutocomplete
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={formatOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={loading}
      filterOptions={x => x}
      placeholder={placeholder}
      className={className}
      textFieldProps={textFieldProps}
    />
  );
};
